import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Upload, Trash2, Loader2, ArrowLeft, Lock } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CATEGORIES = ["Kitchen", "Wardrobe", "Living room", "Bedroom", "Full home", "Other"];

export default function AdminUpload() {
  const [pin, setPin] = useState(sessionStorage.getItem("sae_admin_pin") || "");
  const [authed, setAuthed] = useState(false);
  const [pinInput, setPinInput] = useState("");
  const [photos, setPhotos] = useState([]);
  const [file, setFile] = useState(null);
  const [caption, setCaption] = useState("");
  const [category, setCategory] = useState(CATEGORIES[0]);
  const [busy, setBusy] = useState(false);
  const [checking, setChecking] = useState(false);
  const fileRef = useRef(null);

  const loadPhotos = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/photos`);
      setPhotos(res.data || []);
    } catch {
      toast.error("Could not load photos.");
    }
  }, []);

  const verify = useCallback(
    async (candidate) => {
      setChecking(true);
      try {
        await axios.get(`${API}/admin/verify`, { headers: { "X-Admin-PIN": candidate } });
        sessionStorage.setItem("sae_admin_pin", candidate);
        setPin(candidate);
        setAuthed(true);
      } catch {
        sessionStorage.removeItem("sae_admin_pin");
        setAuthed(false);
        if (candidate) toast.error("Wrong PIN. Try again.");
      } finally {
        setChecking(false);
      }
    },
    []
  );

  useEffect(() => {
    if (pin) verify(pin);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (authed) loadPhotos();
  }, [authed, loadPhotos]);

  const upload = async (e) => {
    e.preventDefault();
    if (!file) {
      toast.error("Choose a photo first.");
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("caption", caption);
      fd.append("category", category);
      await axios.post(`${API}/photos/upload`, fd, { headers: { "X-Admin-PIN": pin } });
      toast.success("Photo uploaded — it's live in the gallery.");
      setFile(null);
      setCaption("");
      if (fileRef.current) fileRef.current.value = "";
      loadPhotos();
    } catch (err) {
      if (err?.response?.status === 401) {
        setAuthed(false);
        toast.error("Session expired — enter the PIN again.");
      } else {
        toast.error(err?.response?.data?.detail || "Upload failed. Try a JPG/PNG/WebP under 10 MB.");
      }
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    try {
      await axios.delete(`${API}/photos/${id}`, { headers: { "X-Admin-PIN": pin } });
      toast.success("Photo removed from the gallery.");
      setPhotos((p) => p.filter((x) => x.id !== id));
    } catch {
      toast.error("Could not delete. Try again.");
    }
  };

  return (
    <div className="min-h-screen bg-paper font-sans text-ink" data-testid="admin-page">
      <header className="border-b border-line">
        <div className="mx-auto flex max-w-3xl items-center justify-between px-6 py-5">
          <a href="/" data-testid="admin-home-link" className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.25em] text-taupe transition-colors duration-300 hover:text-ink">
            <ArrowLeft size={14} />
            STAR AVG — Studio
          </a>
          {authed && (
            <button
              data-testid="admin-logout-button"
              onClick={() => {
                sessionStorage.removeItem("sae_admin_pin");
                setPin("");
                setAuthed(false);
              }}
              className="text-xs uppercase tracking-[0.25em] text-taupe transition-colors duration-300 hover:text-ink"
            >
              Log out
            </button>
          )}
        </div>
      </header>
      <main className="mx-auto max-w-3xl px-6 py-16">
        {!authed ? (
          <form
            data-testid="admin-pin-form"
            onSubmit={(e) => {
              e.preventDefault();
              verify(pinInput.trim());
            }}
            className="mx-auto max-w-sm pt-16"
          >
            <Lock size={22} className="mb-6 text-clay" />
            <h1 className="font-serif text-3xl tracking-tight">Studio access</h1>
            <p className="mt-3 text-sm leading-relaxed text-taupe">
              Enter the studio PIN to manage the project photos shown in the
              website gallery.
            </p>
            <input
              data-testid="admin-pin-input"
              type="password"
              value={pinInput}
              onChange={(e) => setPinInput(e.target.value)}
              placeholder="Studio PIN"
              className="mt-8 w-full border-b border-line bg-transparent py-3 text-base outline-none transition-colors duration-300 focus:border-ink"
            />
            <button
              type="submit"
              data-testid="admin-login-button"
              disabled={checking || !pinInput.trim()}
              className="mt-8 flex items-center gap-3 bg-ink px-8 py-4 text-xs font-semibold uppercase tracking-[0.2em] text-paper transition-colors duration-300 hover:bg-clay hover:text-ink disabled:opacity-50"
            >
              {checking ? <Loader2 size={14} className="animate-spin" /> : "Enter"}
            </button>
          </form>
        ) : (
          <>
            <h1 className="font-serif text-3xl tracking-tight">Project photos</h1>
            <p className="mt-3 text-sm leading-relaxed text-taupe">
              Upload completed project photos — they appear in the website
              gallery under "Recent work by the studio" immediately.
            </p>
            <form data-testid="photo-upload-form" onSubmit={upload} className="mt-10 space-y-8 border border-line bg-sand p-8">
              <div>
                <label className="mb-2 block text-[11px] font-semibold uppercase tracking-[0.3em] text-taupe">
                  Photo (JPG, PNG or WebP — max 10 MB)
                </label>
                <input
                  ref={fileRef}
                  data-testid="photo-file-input"
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  onChange={(e) => setFile(e.target.files?.[0] || null)}
                  className="w-full text-sm file:mr-4 file:border-0 file:bg-ink file:px-5 file:py-3 file:text-xs file:font-semibold file:uppercase file:tracking-[0.15em] file:text-paper"
                />
              </div>
              <div>
                <label htmlFor="photo-caption" className="mb-2 block text-[11px] font-semibold uppercase tracking-[0.3em] text-taupe">
                  Caption
                </label>
                <input
                  id="photo-caption"
                  data-testid="photo-caption-input"
                  value={caption}
                  onChange={(e) => setCaption(e.target.value)}
                  maxLength={140}
                  placeholder="e.g. L-shaped modular kitchen, Greater Noida West"
                  className="w-full border-b border-line bg-transparent py-3 text-base outline-none transition-colors duration-300 focus:border-ink placeholder:text-taupe/60"
                />
              </div>
              <div>
                <label htmlFor="photo-category" className="mb-2 block text-[11px] font-semibold uppercase tracking-[0.3em] text-taupe">
                  Category
                </label>
                <select
                  id="photo-category"
                  data-testid="photo-category-select"
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  className="w-full cursor-pointer appearance-none border-b border-line bg-transparent py-3 text-base outline-none"
                >
                  {CATEGORIES.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </div>
              <button
                type="submit"
                data-testid="photo-upload-button"
                disabled={busy}
                className="flex items-center gap-3 bg-ink px-8 py-4 text-xs font-semibold uppercase tracking-[0.2em] text-paper transition-colors duration-300 hover:bg-clay hover:text-ink disabled:opacity-50"
              >
                {busy ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
                {busy ? "Uploading..." : "Upload to gallery"}
              </button>
            </form>
            <h2 className="mt-16 mb-8 text-[11px] font-semibold uppercase tracking-[0.35em] text-taupe">
              Live in the gallery ({photos.length})
            </h2>
            <div data-testid="admin-photo-grid" className="grid grid-cols-1 gap-8 sm:grid-cols-2">
              {photos.map((p) => (
                <figure key={p.id} className="border border-line">
                  <img
                    src={`${API}/photos/image/${p.id}`}
                    alt={p.caption || "Project photo"}
                    loading="lazy"
                    className="aspect-[4/3] w-full object-cover"
                  />
                  <figcaption className="flex items-center justify-between gap-4 px-4 py-3">
                    <span className="truncate text-xs text-taupe">{p.caption || "Recent project"}</span>
                    <button
                      data-testid={`photo-delete-${p.id}`}
                      onClick={() => remove(p.id)}
                      aria-label="Delete photo"
                      className="shrink-0 text-taupe transition-colors duration-300 hover:text-red-700"
                    >
                      <Trash2 size={15} />
                    </button>
                  </figcaption>
                </figure>
              ))}
              {photos.length === 0 && (
                <p className="text-sm text-taupe">No project photos yet — upload your first one above.</p>
              )}
            </div>
          </>
        )}
      </main>
    </div>
  );
}
